
import { Box, Grid, Modal, Typography, Button } from '@mui/material';
import '../App.css';
import { BackgroundStyle, BigHeading, ListContainer, ShortBlueButton } from '../assets/style';
import MainContent from '../components/MainContent';
import { tsingYiNortheastPark } from '../assets/imgs';
import styled from '@emotion/styled';
import Map from "../components/MapBase";
import { useEffect, useState } from 'react';
import { schedulePageName } from '../components/PageName';
import { useNavigate } from 'react-router-dom';
import MapLabel from '../components/MapLabel';
import { useLazyGetTodayScheduleQuery } from '../features/schedule/scheduleSlice';
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";


const MainPageRedSmallBox = styled.div`
    background: linear-gradient(180deg,rgb(255, 0, 0) 20.74%, rgba(255, 255, 255, 0.4) 100%);
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    height: 70px;
    padding: 15px 47px 15px 16px;
    font-size: calc(18px + 0.4vw);
    font-family: 'Poppins';
    font-weight: 500;
    display: flex;
    align-items: center;
`

const MainPageGreenSmallBox = styled.div`
    background: linear-gradient(180deg, #C9FF72 20.74%, rgba(255, 255, 255, 0.4) 100%);
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    height: 70px;
    padding: 15px 47px 15px 16px;
    font-size: calc(18px + 0.4vw);
    font-family: 'Poppins';
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-left: calc(30px + 2vw);
`

const MainPageSmallBox = styled.div`
    background: linear-gradient(180deg, #FFC672 25.81%, rgba(255, 255, 255, 0.4) 100%);
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
    border-radius: 10px;
    height: 70px;
    padding: 15px 47px 15px 16px;
    font-size: calc(18px + 0.4vw);
    font-family: 'Poppins';
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-left: calc(30px + 2vw);
    @media (max-width: 600px) {
        margin: 0;
    }
`

const MainPageLargeText = styled.span`
    font-size: calc(42px + 0.4vw);
`

const MainPageBoxesContainer = styled.div`
    display: flex;
    justify-content: center;
    @media (max-width: 600px) {
        display: block;
    }
`

const mainPageRedBoxStyle = {
    background: 'linear-gradient(180deg, rgba(255, 0, 0, 0.6) 0%, rgba(255, 255, 255, 0.6) 100%)',
    margin: '20px 10px 0px',
    padding: '17px 19px',
    borderRadius: '15px',
    width: 'auto'
}

const mainPageGreenBoxStyle = {
    background: 'linear-gradient(180deg, rgba(201, 255, 114, 0.6) 0%, rgba(255, 255, 255, 0.6) 100%)',
    margin: '20px 10px 0px',
    padding: '17px 19px',
    borderRadius: '15px',
    width: 'auto'
}

const mainPageBoxStyle = {
    background: 'linear-gradient(180deg, rgba(255, 198, 114, 0.6) 0%, rgba(255, 255, 255, 0.6) 100%);',
    padding: '17px 19px',
    borderRadius: '15px',
    margin: '20px 10px 37px',
    width: 'auto'
}

const MainPageSectionHeading = styled.span`
    text-decoration-line: underline;
    font-family: 'Poppins';
    font-size: calc(12px + 0.4vw);
`

const mainPageWhiteSmallBoxStyle = {
    background: 'white',
    padding: '13px 10px 2px 10px',
    borderRadius: '17px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
}

const MainPageWhiteSmallBoxImage = styled.img`
    width: 100%;
    height: 300px;
    object-fit: cover;
`

const MainPageWhiteSmallBoxMapContainer = styled.div`
    width: 100%;
    height: 300px;
`

const MainPageProjectPropertyParagraph = styled.p`
    margin: 0px 5px;
    font-size: 12px;
    font-weight: 400;
`

// Dialog style
const RiskAssessmentDialogContainer = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 600px;
    max-width: 90vw;
    background: #E9F5FF;
    border-radius: 20px;
    box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);
    padding: 30px;
`;

const DialogTitle = styled(Typography)`
    font-family: 'Poppins';
    font-weight: 600;
    font-size: 24px;
    color: #0F2C57;
    margin-bottom: 20px;
    text-align: center;
`;

const DialogContent = styled(Box)`
    background: rgba(255, 255, 255, 0.8);
    border-radius: 15px;
    padding: 20px;
    margin-bottom: 20px;
`;

const DialogButtonContainer = styled(Box)`
    display: flex;
    justify-content: center;
    gap: 20px;
`;

// Dialog component
const RiskAssessmentDialog = ({ open, onClose, project }: {
    open: boolean,
    onClose: () => void,
    project: any
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="risk-assessment-dialog"
        >
            <RiskAssessmentDialogContainer>
                <DialogTitle>
                    {t("Not Allowed to Work")}
                </DialogTitle>
                <DialogContent>
                    <MapLabel title={t("WorkType")} value={project?.workType} />
                    <MapLabel title={t("Start Time")} value={project?.startTime} />
                    <MapLabel title={t("End Time")} value={project?.endTime} />
                    <MapLabel title={t("Project No")} value={project?.projectNumber} />
                    <MapLabel title={t("Project")} value={project?.projectName} />
                    <MapLabel title={t("Location")} value={project?.location} />
                    <MapLabel title={t("Worker")} value={project?.workers} />
                    <MapLabel title={t("Status")} value={t(project?.status)} />
                </DialogContent>
                <DialogButtonContainer>
                    <ShortBlueButton onClick={onClose}>
                        {t("Close")}
                    </ShortBlueButton>
                    <ShortBlueButton onClick={() => {
                        navigate('/entry-detail', {
                            state: {
                                entry: {
                                    id: project.id,
                                    projectName: project.projectName,
                                    lat: project.lat,
                                    lng: project.lng,
                                    location: project.location,
                                    status: project.status
                                }
                            }
                        });
                        onClose();
                    }}>
                        {t("Work Schedule")}
                    </ShortBlueButton>
                </DialogButtonContainer>
            </RiskAssessmentDialogContainer>
        </Modal>
    );
};

// GridItem component
const GridItem = function (props: {
    lat: string,
    lng: string,
    endTime: string,
    startTime: string,
    projectNumber: string,
    projectName: string,
    location: string,
    workers: string,
    onClick?: any,
    workType: string,
    isMissingRiskAssessment?: boolean,
    onMissingRiskAssessmentClick?: (project: any) => void
}) {
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();
    console.log("props, ", props)
    const coordinates = `${props.lat},${props.lng}`

    const handleClick = () => {
        if (props.isMissingRiskAssessment && props.onMissingRiskAssessmentClick) {
            props.onMissingRiskAssessmentClick(props);
        } else {
            navigate('/real-time', { state: { schedule: props } });
        }
    };

    return (
        <Grid item xs={12} sm={3.9} sx={mainPageWhiteSmallBoxStyle} >
            <MainPageWhiteSmallBoxMapContainer onClick={handleClick}>
                <Map
                    zoom={19}
                    coordinates={coordinates}
                    name={props.location}
                    isInput={false}
                />
            </MainPageWhiteSmallBoxMapContainer>
            <MapLabel title={t("WorkType")} value={props.workType}></MapLabel>
            <MapLabel title={t("Start Time")} value={props?.startTime}></MapLabel>
            <MapLabel title={t("End Time")} value={props?.endTime}></MapLabel>
            <MapLabel title={t("Project No")} value={props?.projectNumber}></MapLabel>
            <MapLabel title={t("Project")} value={props?.projectName}></MapLabel>
            <MapLabel title={t("Location")} value={props?.location}></MapLabel>
            <MapLabel title={t("Worker")} value={props?.workers}></MapLabel>
        </Grid>
    )
}

const TodaysSchedulePage = () => {
    const {t, i18n} = useTranslation();
    const [getScheduleQuery, schedules] = useLazyGetTodayScheduleQuery();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<any>(null);

    // const date = '2024-03-26' //test
    const date = dayjs().format("DD/MM/YYYY HH:mm:ss") //test
    useEffect(() => {
        handleScheduleData(date)//test
        console.log("TodaysSchedulePage schedules, ", schedules)
    }, [])

    const handleScheduleData = async (date?: string) => {
        try {
            getScheduleQuery({ dateTime: date })
                .then((response) => {
                    if (response.status == 'fulfilled') {
                        // console.log("response?.data?.Resultttttttttt, ", response?.data)
                    }
                })
        } catch (error) {

        } finally {

        }
    }

    const handleMissingRiskAssessmentClick = (project: any) => {
        setSelectedProject(project);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedProject(null);
    };

    return (
        <MainContent type={schedulePageName}>
            <BackgroundStyle>
                <ListContainer>
                    <BigHeading>{t("Today's Work Schedule")}</BigHeading>
                    <MainPageBoxesContainer>
                        <MainPageRedSmallBox>{t("Not Allowed to Work")}: &nbsp;<MainPageLargeText>{schedules?.data?.missingRiskAssessmentOrWorkPermit.length}</MainPageLargeText></MainPageRedSmallBox>
                        <MainPageGreenSmallBox>{t("To be Complete")}:&nbsp;<MainPageLargeText>{schedules?.data?.toBeCompleted.length}</MainPageLargeText></MainPageGreenSmallBox>
                        <MainPageSmallBox>{t("Remaining Work Total")}: &nbsp;<MainPageLargeText>{schedules?.data?.remainingWork.length}</MainPageLargeText></MainPageSmallBox>
                    </MainPageBoxesContainer>

                    {/* To-be-completed works' details*/}
                    <Grid container sx={mainPageRedBoxStyle} columnGap={2} rowGap={1}>
                        <Grid item xs={12}>
                            <MainPageSectionHeading>{t("Not Allowed to Work")}</MainPageSectionHeading>
                        </Grid>
                        {
                            schedules?.data?.missingRiskAssessmentOrWorkPermit.map((project: any, index: any) => {
                                return (
                                    <GridItem
                                        key={index}
                                        {...project}
                                        isMissingRiskAssessment={true}
                                        onMissingRiskAssessmentClick={handleMissingRiskAssessmentClick}
                                    />
                                )
                            })
                        }
                    </Grid>

                    {/* To-be-completed works' details*/}
                    <Grid container sx={mainPageGreenBoxStyle} columnGap={2} rowGap={1}>
                        <Grid item xs={12}>
                            <MainPageSectionHeading>{t("To be Complete")}</MainPageSectionHeading>
                        </Grid>
                        {
                            schedules?.data?.toBeCompleted.map((project: any, index: any) => {
                                return <GridItem key={index} {...project} />
                            })
                        }
                    </Grid>

                    {/* Remaining works' details*/}
                    <Grid container sx={mainPageBoxStyle} columnGap={2} rowGap={1}>
                        <Grid item xs={12}>
                            <MainPageSectionHeading>{t("Remaining Work Total")}</MainPageSectionHeading>
                        </Grid>
                        {
                            schedules?.data?.remainingWork.map((project: any, index: any) => {
                                return <GridItem key={index} {...project} />
                            })
                        }
                    </Grid>
                </ListContainer>
            </BackgroundStyle>

            {/* 風險評估對話框 */}
            <RiskAssessmentDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                project={selectedProject}
            />
        </MainContent>
    );
};

export default TodaysSchedulePage;